import { User, UserOrg } from "@/models/user.tsx";
import { SecurityRole } from "@/models.ts";

export type PermissionLevel = "none" | "partial" | "all";

export const STATEWIDE = "All / Statewide";
const STATEWIDE_ORG_TYPE = 2;
const ADMIN_ROLE_CODE = "ADMIN";

export interface UserAccessRow {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  phoneExtension: string | null;
  isStateLevel: boolean;
  organization: string;
  isAdmin: boolean;
  viewLevel: PermissionLevel;
  editLevel: PermissionLevel;
  additionalLevel: PermissionLevel;
}

export interface RoleGroups {
  adminIds: Set<number>;
  viewIds: Set<number>;
  editIds: Set<number>;
  additionalIds: Set<number>;
}

const EMPTY_ROLE_GROUPS: RoleGroups = {
  adminIds: new Set(),
  viewIds: new Set(),
  editIds: new Set(),
  additionalIds: new Set(),
};

function classifyRoles(roles: SecurityRole[]): RoleGroups {
  const adminIds = new Set<number>();
  const viewIds = new Set<number>();
  const editIds = new Set<number>();
  const additionalIds = new Set<number>();
  for (const role of roles) {
    const code = role.CAPSDACRoleCode;
    if (code === ADMIN_ROLE_CODE) {
      adminIds.add(role.CAPSDACRoleId);
    } else if (code.endsWith("View")) {
      viewIds.add(role.CAPSDACRoleId);
    } else if (code.endsWith("Edit")) {
      editIds.add(role.CAPSDACRoleId);
    } else {
      additionalIds.add(role.CAPSDACRoleId);
    }
  }
  return { adminIds, viewIds, editIds, additionalIds };
}

export function buildRoleGroupsByOrgType(
  securityRoles: SecurityRole[]
): Map<number, RoleGroups> {
  const rolesByGrpKey = new Map<number, SecurityRole[]>();
  for (const role of securityRoles) {
    const grpKey = role.ScrtyGrpKey;
    const list = rolesByGrpKey.get(grpKey) ?? [];
    list.push(role);
    rolesByGrpKey.set(grpKey, list);
  }
  const result = new Map<number, RoleGroups>();
  for (const [grpKey, roles] of rolesByGrpKey) {
    result.set(grpKey, classifyRoles(roles));
  }
  return result;
}

function computePermissionLevel(
  roleIds: number[],
  group: Set<number>
): PermissionLevel {
  let matches = 0;
  for (const id of roleIds) {
    if (group.has(id)) matches++;
  }
  if (matches === 0) return "none";
  if (matches === group.size) return "all";
  return "partial";
}

export function toUserAccessRows(
  users: User<UserOrg>[],
  roleGroupsByOrgType: Map<number, RoleGroups>
): UserAccessRow[] {
  return users.flatMap((user) =>
    user.userOrgs.map((org): UserAccessRow => {
      const isStateLevel = org.orgType === STATEWIDE_ORG_TYPE;
      const roleGroups =
        roleGroupsByOrgType.get(org.orgType) ?? EMPTY_ROLE_GROUPS;
      return {
        id: String(org.userOrgId),
        username: user.userName,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        phoneExtension: user.phoneExtension || null,
        isStateLevel,
        organization: isStateLevel ? STATEWIDE : org.orgName,
        isAdmin: org.roleIds.some((r) => roleGroups.adminIds.has(r)),
        viewLevel: computePermissionLevel(org.roleIds, roleGroups.viewIds),
        editLevel: computePermissionLevel(org.roleIds, roleGroups.editIds),
        additionalLevel: computePermissionLevel(
          org.roleIds,
          roleGroups.additionalIds
        ),
      };
    })
  );
}

export const PERMISSION_LABELS: Record<PermissionLevel, string> = {
  none: "None",
  partial: "Partial",
  all: "All",
};

export function formatUserPhone(row: UserAccessRow): string {
  if (!row.phoneNumber) return "";
  return row.phoneExtension
    ? `${row.phoneNumber} x${row.phoneExtension}`
    : row.phoneNumber;
}

const yn = (v: boolean) => (v ? "Yes" : "");

export function toExportRows(rows: UserAccessRow[]) {
  return rows.map((r) => ({
    username: r.username,
    firstName: r.firstName,
    lastName: r.lastName,
    email: r.email,
    phone: formatUserPhone(r),
    stateLevel: yn(r.isStateLevel),
    organization: r.organization,
    admin: yn(r.isAdmin),
    view: PERMISSION_LABELS[r.viewLevel],
    edit: PERMISSION_LABELS[r.editLevel],
    additional: PERMISSION_LABELS[r.additionalLevel],
  }));
}

export const CSV_HEADERS = [
  { label: "Username", key: "username" },
  { label: "First Name", key: "firstName" },
  { label: "Last Name", key: "lastName" },
  { label: "Email Address", key: "email" },
  { label: "Phone Number", key: "phone" },
  { label: "Organization", key: "organization" },
  { label: "State Level", key: "stateLevel" },
  { label: "Administrator", key: "admin" },
  { label: "View", key: "view" },
  { label: "Edit", key: "edit" },
  { label: "Additional", key: "additional" },
] as const;
